import { Route, Router } from "@angular/router";

import { DsV3Plan, notExternalEntity, notPmoEntity, pmoEntity } from "../../../core/guards";
import { PageMode } from "../../../shared/constants/enums";
import { manageTPCyclesPageGuards } from "../../transformational-projects/_guards";
import {
  createCyclesPageGuards,
  createSectorsPageGuards,
  manageSectorsPageGuards,
  projectTypeCreateAccessGuard,
  projectTypeEditAccessGuard,
  projectTypeViewAccessGuard,
  viewCyclesPageGuards,
  viewSectorsPageGuards,
} from "./projects-utils";

const CYCLES_ROUTES: Route[] = [
  {
    path: "cycles",
    canActivate: [DsV3Plan(), pmoEntity, ...viewCyclesPageGuards],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("../../transformational-projects/cycles/tp-cycles-list/tp-cycles-list").then(
            (e) => e.TpCyclesListComponent
          ),
      },
      {
        path: "create",
        loadComponent: () =>
          import("../../transformational-projects/cycles/tp-cycle-editor/tp-cycle-editor").then(
            (e) => e.TpCycleEditorComponent
          ),
        canActivate: [...createCyclesPageGuards],
        data: {
          breadcrumb: "national_projects.cycles.create_tp_cycle",
          title: "national_projects.cycles.create_tp_cycle",
          pageMode: PageMode.create,
        },
      },
      {
        path: "edit/:id",
        loadComponent: () =>
          import("../../transformational-projects/cycles/tp-cycle-editor/tp-cycle-editor").then(
            (e) => e.TpCycleEditorComponent
          ),
        canActivate: [...manageTPCyclesPageGuards],
        data: {
          breadcrumb: "national_projects.cycles.edit_tp_cycle",
          title: "national_projects.cycles.edit_tp_cycle",
          pageMode: PageMode.edit,
        },
      },
      {
        path: ":id",
        loadComponent: () =>
          import("../../transformational-projects/cycles/tp-cycle-editor/tp-cycle-editor").then(
            (e) => e.TpCycleEditorComponent
          ),
        data: {
          breadcrumb: "national_projects.cycles.view_tp_cycle",
          title: "national_projects.cycles.view_tp_cycle",
          pageMode: PageMode.view,
        },
      },
    ],
    data: {
      breadcrumb: "national_projects.cycles.tp_cycles",
      title: "national_projects.cycles.tp_cycles",
    },
  },
];

const SECTORS_ROUTES: Route[] = [
  {
    path: "sectors",
    canActivate: [DsV3Plan(), pmoEntity, ...viewSectorsPageGuards],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("../../transformational-projects/sectors/tp-sectors-list/tp-sectors-list").then(
            (e) => e.TpSectorsListComponent
          ),
      },
      {
        path: "create",
        loadComponent: () =>
          import("../../transformational-projects/sectors/tp-sectors-editor/tp-sectors-editor").then(
            (e) => e.TpSectorsEditorComponent
          ),
        canActivate: [...createSectorsPageGuards],
        data: {
          breadcrumb: "national_projects.national_sector.editor.create",
          title: "national_projects.national_sector.editor.create",
          pageMode: PageMode.create,
        },
      },
      {
        path: "edit/:id",
        loadComponent: () =>
          import("../../transformational-projects/sectors/tp-sectors-editor/tp-sectors-editor").then(
            (e) => e.TpSectorsEditorComponent
          ),
        canActivate: [...manageSectorsPageGuards],
        data: {
          breadcrumb: "national_projects.national_sector.editor.edit",
          title: "national_projects.national_sector.editor.edit",
          pageMode: PageMode.edit,
        },
      },
      {
        path: ":id",
        loadComponent: () =>
          import("../../transformational-projects/sectors/tp-sectors-editor/tp-sectors-editor").then(
            (e) => e.TpSectorsEditorComponent
          ),
        data: {
          breadcrumb: "national_projects.national_sector.editor.view",
          title: "national_projects.national_sector.editor.view",
          pageMode: PageMode.view,
        },
      },
    ],
    data: {
      breadcrumb: "national_projects.national_sector.title",
      title: "national_projects.national_sector.title",
    },
  },
];

const AGREEMENTS_ROUTES: Route[] = [
  {
    path: "agreements",
    canActivate: [DsV3Plan(), notExternalEntity],
    loadComponent: () =>
      import("../../transformational-projects/agreements/agreements").then((e) => e.AgreementsComponent),
    data: {
      breadcrumb: "national_projects.agreements.title",
      title: "national_projects.agreements.title",
    },
  },
];

export const PROJECTS_DS_V3_ROUTES: Route[] = [
  {
    path: "project",
    data: {
      breadcrumb: "national_projects.projects",
    },
    children: [
      ...CYCLES_ROUTES,
      ...SECTORS_ROUTES,
      ...AGREEMENTS_ROUTES,
      {
        path: "",
        loadComponent: () => import("../../../shared/components").then((e) => e.SubmenuComponent),
        canActivate: [DsV3Plan(), notExternalEntity],
        data: {
          onCycleChange: (router: Router) => router.navigateByUrl("console"),
        },
      },
      {
        path: ":projectType",
        children: [
          {
            path: "",
            loadComponent: () => import("./projects-list/projects-list").then((e) => e.ProjectsListComponent),
            canActivate: [DsV3Plan(), notExternalEntity, projectTypeViewAccessGuard],
            data: {
              title: "ds_v3.projects.title",
              breadcrumb: {
                alias: "projectsList",
              },
            },
          },
          {
            path: "edit/:projectId",
            loadComponent: () => import("./projects-editor/projects-editor").then((e) => e.ProjectsEditorComponent),
            canActivate: [DsV3Plan(), notExternalEntity, projectTypeEditAccessGuard],
            data: {
              title: "ds_v3.projects.title",
              breadcrumb: {
                alias: "projectsEditor",
              },
              pageMode: PageMode.edit,
            },
          },
          {
            path: "create",
            loadComponent: () => import("./projects-editor/projects-editor").then((e) => e.ProjectsEditorComponent),
            canActivate: [DsV3Plan(), notPmoEntity, notExternalEntity, projectTypeCreateAccessGuard],
            data: {
              title: "ds_v3.projects.title",
              breadcrumb: {
                alias: "projectsEditor",
              },
              pageMode: PageMode.create,
            },
          },
          {
            path: ":projectId",
            loadComponent: () => import("./projects-view/projects-view").then((e) => e.ProjectsViewComponent),
            canActivate: [projectTypeViewAccessGuard],
            data: {
              title: "ds_v3.projects.title",
              breadcrumb: {
                alias: "projectsView",
              },
            },
          },
        ],
      },
    ],
  },
];
